import {
  type ColumnDef,
  type FilterFn,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  ImageIcon,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'

import type { Company } from '../../types/company'

interface CompaniesTableProps {
  companies: Company[]
  loading: boolean
  error: string | null
  onRetry: () => void
  onEdit: (company: Company) => void
  onDelete: (company: Company) => void
}

const dateFormatter = new Intl.DateTimeFormat('es-CO', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function searchText(company: Company) {
  let domain = ''
  try {
    domain = new URL(company.website_url).hostname
  } catch {
    domain = company.website_url
  }

  return [
    company.name,
    company.business_activity,
    company.website_url,
    domain,
    ...company.emails.map(({ email }) => email),
  ]
    .join(' ')
    .toLocaleLowerCase('es')
}

const companyGlobalFilter: FilterFn<Company> = (row, _columnId, filterValue: unknown) => {
  const terms = String(filterValue).trim().toLocaleLowerCase('es').split(/\s+/u).filter(Boolean)
  const haystack = searchText(row.original)
  return terms.every((term) => haystack.includes(term))
}

function SortIcon({ direction }: { direction: false | 'asc' | 'desc' }) {
  if (direction === 'asc') return <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />
  if (direction === 'desc') return <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
  return <ArrowUpDown aria-hidden="true" className="h-3.5 w-3.5 opacity-45" />
}

function TableSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-2xl border bg-white"
      aria-label="Cargando empresas"
      aria-busy="true"
    >
      <div className="h-12 animate-pulse border-b bg-ink/5" />
      {Array.from({ length: 5 }, (_, index) => (
        <div
          key={index}
          className="grid grid-cols-4 gap-4 border-b p-4 last:border-0 lg:grid-cols-7"
        >
          {Array.from({ length: 7 }, (_item, cell) => (
            <div key={cell} className="h-5 animate-pulse rounded bg-ink/10" />
          ))}
        </div>
      ))}
    </div>
  )
}

export function CompaniesTable({
  companies,
  loading,
  error,
  onRetry,
  onEdit,
  onDelete,
}: CompaniesTableProps) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updated_at', desc: true }])
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })
  const [exporting, setExporting] = useState(false)

  const columns = useMemo<ColumnDef<Company>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Empresa',
        cell: ({ row }) => <span className="font-semibold text-ink">{row.original.name}</span>,
      },
      {
        accessorKey: 'business_activity',
        header: 'Actividad o tipo',
        cell: ({ row }) => <span className="text-ink/75">{row.original.business_activity}</span>,
      },
      {
        accessorKey: 'website_url',
        header: 'Sitio web',
        enableSorting: false,
        cell: ({ row }) => (
          <a
            href={row.original.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-52 items-center gap-1.5 truncate font-semibold text-moss underline decoration-moss/25 underline-offset-4 hover:decoration-moss"
            title={row.original.website_url}
          >
            <span className="truncate">{new URL(row.original.website_url).hostname}</span>
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          </a>
        ),
      },
      {
        id: 'emails',
        header: 'Correos',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex min-w-48 flex-wrap gap-1.5">
            {row.original.emails.map(({ id, email }) => (
              <a
                key={id}
                href={`mailto:${email}`}
                className="max-w-56 truncate rounded-lg bg-mist px-2 py-1 text-xs font-semibold text-moss hover:bg-moss hover:text-white"
                title={email}
              >
                {email}
              </a>
            ))}
          </div>
        ),
      },
      {
        id: 'image',
        header: 'Imagen',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.image ? (
            <div className="flex min-w-32 items-center gap-2.5">
              <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg border bg-white">
                <img
                  src={row.original.image.public_url}
                  alt={`Imagen de ${row.original.name}`}
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              </div>
              <a
                href={row.original.image.public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold text-moss underline decoration-moss/25 underline-offset-4"
              >
                Ver imagen
              </a>
            </div>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink/45">
              <ImageIcon aria-hidden="true" className="h-4 w-4" /> Sin imagen
            </span>
          ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Última modificación',
        sortingFn: 'datetime',
        cell: ({ row }) => (
          <time
            className="whitespace-nowrap text-xs text-ink/60"
            dateTime={row.original.updated_at}
          >
            {dateFormatter.format(new Date(row.original.updated_at))}
          </time>
        ),
      },
      {
        id: 'actions',
        header: 'Acciones',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(row.original)}>
              <Pencil aria-hidden="true" className="h-4 w-4" /> Editar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50 hover:text-red-800"
              onClick={() => onDelete(row.original)}
            >
              <Trash2 aria-hidden="true" className="h-4 w-4" /> Eliminar
            </Button>
          </div>
        ),
      },
    ],
    [onDelete, onEdit],
  )

  const table = useReactTable({
    data: companies,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    globalFilterFn: companyGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const filteredCompanies = table.getPrePaginationRowModel().rows.map(({ original }) => original)

  const exportRows = async () => {
    if (filteredCompanies.length === 0) return
    setExporting(true)
    try {
      const { downloadCompaniesExcel } = await import('../../lib/excel')
      const filename = await downloadCompaniesExcel(filteredCompanies)
      toast.success('Excel generado', {
        description: `${filename} incluye ${filteredCompanies.length} empresa(s).`,
      })
    } catch (exportError) {
      toast.error('No se pudo exportar', {
        description: exportError instanceof Error ? exportError.message : 'Inténtalo de nuevo.',
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="surface-card p-4 sm:p-6" aria-labelledby="companies-title">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-moss">
            Directorio compartido
          </p>
          <h2
            id="companies-title"
            className="mt-1 font-display text-2xl font-bold tracking-tight sm:text-3xl"
          >
            Empresas registradas
          </h2>
          <p className="mt-1 text-sm text-ink/55">
            {filteredCompanies.length} de {companies.length} empresa
            {companies.length === 1 ? '' : 's'}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-64 flex-1 sm:w-80">
            <label htmlFor="company-search" className="form-label">
              Buscar en el directorio
            </label>
            <div className="relative">
              <Search
                aria-hidden="true"
                className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40"
              />
              <input
                id="company-search"
                type="search"
                className="form-input pl-10"
                placeholder="Nombre, actividad, dominio o correo"
                value={globalFilter}
                onChange={(event) => {
                  setGlobalFilter(event.target.value)
                  setPagination((current) => ({ ...current, pageIndex: 0 }))
                }}
              />
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => void exportRows()}
            disabled={filteredCompanies.length === 0 || exporting || loading}
            aria-describedby={filteredCompanies.length === 0 ? 'export-disabled-reason' : undefined}
          >
            {exporting ? <Spinner /> : <Download aria-hidden="true" className="h-4 w-4" />}
            {exporting ? 'Generando…' : 'Exportar a Excel'}
          </Button>
        </div>
      </div>

      {filteredCompanies.length === 0 && !loading && (
        <p id="export-disabled-reason" className="mt-3 text-right text-xs font-medium text-ink/50">
          No hay coincidencias para exportar.
        </p>
      )}

      <div className="mt-5">
        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center" role="alert">
            <p className="font-semibold text-red-900">No pudimos cargar las empresas.</p>
            <p className="mt-1 text-sm text-red-800/75">{error}</p>
            <Button variant="secondary" className="mt-4" onClick={onRetry}>
              <RefreshCw aria-hidden="true" className="h-4 w-4" /> Reintentar
            </Button>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-white/60 px-6 py-14 text-center">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-mist text-moss">
              <Search aria-hidden="true" className="h-5 w-5" />
            </span>
            <p className="mt-4 font-display text-xl font-bold">
              {companies.length === 0 ? 'Aún no hay empresas' : 'No encontramos coincidencias'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-ink/55">
              {companies.length === 0
                ? 'Completa el formulario para crear el primer registro del directorio.'
                : 'Prueba con otro nombre, actividad, dominio o correo.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-2xl border bg-white">
              <table className="w-full min-w-[1080px] border-collapse text-left text-sm">
                <thead className="bg-mist/70 text-xs font-bold uppercase tracking-wide text-ink/65">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th key={header.id} className="border-b px-4 py-3.5" scope="col">
                          {header.isPlaceholder ? null : header.column.getCanSort() ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 rounded-md hover:text-ink"
                              onClick={header.column.getToggleSortingHandler()}
                              aria-label={`Ordenar por ${String(header.column.columnDef.header)}`}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <SortIcon direction={header.column.getIsSorted()} />
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0 hover:bg-mist/25">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-4 align-top">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex items-center gap-2 text-sm text-ink/60">
                Filas por página
                <select
                  className="rounded-lg border bg-white px-2 py-1.5 font-semibold text-ink"
                  value={table.getState().pagination.pageSize}
                  onChange={(event) => table.setPageSize(Number(event.target.value))}
                >
                  {[5, 10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <span className="text-sm text-ink/60">
                  Página{' '}
                  <strong className="text-ink">{table.getState().pagination.pageIndex + 1}</strong>{' '}
                  de <strong className="text-ink">{table.getPageCount()}</strong>
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    aria-label="Página anterior"
                  >
                    <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    aria-label="Página siguiente"
                  >
                    <ChevronRight aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
